import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Database, FileText, Lock, Scale, ShieldCheck } from "lucide-react";

import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { Button } from "@super-resume-new/ui/components/button";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/privacy")({
  head: () =>
    seo({
      title: "Privacy Policy | JobCues",
      description:
        "Privacy Policy for JobCues. Learn how your resume data, personal details, and career information are collected, processed, and secured.",
      path: "/privacy",
    }),
  component: PrivacyPageComponent,
});

const SECTIONS = [
  { id: "section-1", num: "01", title: "Introduction" },
  { id: "section-2", num: "02", title: "Information we collect" },
  { id: "section-3", num: "03", title: "How we use your information" },
  { id: "section-4", num: "04", title: "AI processing and providers" },
  { id: "section-5", num: "05", title: "Data retention and rights" },
  { id: "section-6", num: "06", title: "Cookies and analytics" },
  { id: "section-7", num: "07", title: "Contact and updates" },
];

function PrivacyPageComponent() {
  const lastUpdated = "August 24, 2026";

  return (
    <PageLayout id="privacy-page-content" className="max-w-7xl">
      <PageHeader
        title="Privacy policy"
        description={<>Last updated {lastUpdated}</>}
        icon={ShieldCheck}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-20 flex flex-col gap-5">
            {/* Table of contents */}
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <FileText className="size-3.5 text-muted-foreground" aria-hidden="true" />
                Table of contents
              </h2>
              <nav className="flex flex-col space-y-1">
                {SECTIONS.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <span className="font-mono text-[11px] text-muted-foreground">{sec.num}</span>
                    <span className="truncate">{sec.title}</span>
                  </a>
                ))}
              </nav>
            </div>

            {/* Privacy highlights card */}
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Lock className="size-4 text-violet-500" aria-hidden="true" />
                <h3 className="text-xs font-semibold text-foreground">Zero training guarantee</h3>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                We never sell or rent career data. AI prompts are processed under strict
                zero-retention enterprise agreements and are never used to train public foundation
                models.
              </p>
            </div>

            {/* Terms link card */}
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Scale className="size-4 text-muted-foreground" aria-hidden="true" />
                <h3 className="text-xs font-semibold text-foreground">Terms and conditions</h3>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Review our platform usage guidelines, credit policies, and service terms.
              </p>
              <Link to="/terms" className="mt-1">
                <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                  <span>View terms</span>
                  <ArrowRight className="size-3 text-muted-foreground" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex flex-col gap-6 lg:col-span-8 xl:col-span-9">
          {/* Trust Highlights Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="mb-2 flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-lg border border-border bg-muted">
                  <Lock className="size-4 text-muted-foreground" aria-hidden="true" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">
                  Your career data belongs to you
                </h2>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                We never sell, rent, or trade your uploaded resumes, job applications, or personal
                information to third-party data brokers or recruitment agencies.
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="mb-2 flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-lg border border-border bg-muted">
                  <Database className="size-4 text-muted-foreground" aria-hidden="true" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">Encrypted and safeguarded</h2>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Data transmission is secured with modern TLS encryption, and AI processing is
                conducted under strict zero-retention enterprise privacy standards.
              </p>
            </div>
          </div>

          {/* Section 1 */}
          <section
            id="section-1"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                1
              </span>
              <h2 className="text-sm font-semibold text-foreground">Introduction</h2>
            </div>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              At JobCues (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;), we take your privacy
              seriously. This Privacy Policy describes how we collect, store, use, and protect your
              information when you interact with our web platform, APIs, MCP server, and related
              services.
            </p>
          </section>

          {/* Section 2 */}
          <section
            id="section-2"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                2
              </span>
              <h2 className="text-sm font-semibold text-foreground">Information we collect</h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>We collect the following categories of information:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong className="font-semibold text-foreground">
                    Authentication and profile information:
                  </strong>{" "}
                  When you sign in via OAuth providers (Google, GitHub, X, LinkedIn), we receive
                  your name, email address, profile avatar image, and provider unique identifier.
                </li>
                <li>
                  <strong className="font-semibold text-foreground">Resume and career data:</strong>{" "}
                  Uploaded resume PDFs, extracted text, employment records, educational history,
                  skill lists, project details, and Master Career Profile data.
                </li>
                <li>
                  <strong className="font-semibold text-foreground">
                    Target job and application data:
                  </strong>{" "}
                  Job descriptions you paste or import, match scores, questionnaire answers,
                  application tracker stages, and submission records.
                </li>
                <li>
                  <strong className="font-semibold text-foreground">
                    Usage and device metadata:
                  </strong>{" "}
                  IP addresses, browser headers, operating system info, device identifiers, session
                  cookies, and API interaction logs (used for security, rate-limiting, and
                  anti-abuse verification).
                </li>
                <li>
                  <strong className="font-semibold text-foreground">Payment information:</strong>{" "}
                  Transactions and billing records are managed directly by our payment processor
                  (Dodo Payments). We do not collect or store your raw credit card numbers or
                  banking secrets.
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section
            id="section-3"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                3
              </span>
              <h2 className="text-sm font-semibold text-foreground">How we use your information</h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>We process your data for the following explicit purposes:</p>
              <ul className="list-disc space-y-1.5 pl-5">
                <li>
                  To perform AI-powered resume tailoring, bullet point optimization, and ATS
                  scoring.
                </li>
                <li>To generate PDF downloads and embedded ATS metadata.</li>
                <li>To maintain your application tracking dashboard and saved jobs directory.</li>
                <li>To manage your credit balances, subscriptions, and API key authentications.</li>
                <li>
                  To detect and prevent fraudulent activities, automated scraping, bot attacks, and
                  multi-account free credit abuse.
                </li>
                <li>To provide customer support and troubleshoot technical issues.</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section
            id="section-4"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                4
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                AI processing and third-party service providers
              </h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                To provide advanced resume restructuring and keyword analysis, certain text prompts
                are transmitted to enterprise AI providers (e.g. Cerebras, OpenRouter, Google
                Cloud).
              </p>
              <p>
                Under our enterprise API agreements, your inputs and generated outputs are{" "}
                <strong className="font-semibold text-foreground">
                  not used to train public foundation models
                </strong>
                .
              </p>
              <p>
                We also partner with trusted infrastructure providers for cloud hosting, database
                storage (LibSQL / Turso), authentication, and payment processing. All third parties
                are bound by strict confidentiality and data protection obligations.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section
            id="section-5"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                5
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                Data retention and deletion rights
              </h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                We retain your resume variants, Master Career Profile, and application tracker items
                for as long as your account remains active.
              </p>
              <p>You have the right at any time to:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>Request an export of your career data and resume records.</li>
                <li>Edit or remove specific work experiences, skills, or job tracker items.</li>
                <li>
                  Request full account and associated data deletion by contacting our support team.
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section
            id="section-6"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                6
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                Cookies, analytics, and session management
              </h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                We use cookies and browser storage technologies to deliver core site functionality,
                safeguard security, and analyze platform performance:
              </p>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong className="font-semibold text-foreground">Essential cookies and storage:</strong>{" "}
                  Strictly necessary for user authentication sessions, cross-site request forgery (CSRF)
                  protection, and saving your interface preferences (such as dark or light theme mode).
                </li>
                <li>
                  <strong className="font-semibold text-foreground">Analytics and performance (Google Analytics):</strong>{" "}
                  We use Google Analytics (gtag.js) to understand aggregate traffic volume, navigation patterns,
                  and feature engagement across the app. These metrics help us optimize platform responsiveness
                  and user experience.
                </li>
                <li>
                  <strong className="font-semibold text-foreground">Cookie consent preferences:</strong>{" "}
                  When you first visit JobCues, you can choose to accept all cookies or limit tracking to
                  strictly essential cookies. Your consent preference is stored in your browser and can be
                  updated at any time through our cookie settings.
                </li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section
            id="section-7"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                7
              </span>
              <h2 className="text-sm font-semibold text-foreground">Contact and updates</h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                We may update this Privacy Policy from time to time to reflect enhancements to our
                services or regulatory updates. Any changes will be published here with an updated
                effective date.
              </p>
              <p>
                For privacy inquiries, data requests, or questions, please refer to our{" "}
                <Link
                  to="/terms"
                  className="font-medium text-foreground underline underline-offset-4 hover:text-violet-500"
                >
                  Terms and conditions
                </Link>{" "}
                or contact us via our official support channels.
              </p>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
